<template>
    <b-form-group
        :label-cols-sm="showLabel ? '2' : null"
        :label-cols-lg="showLabel ? '3' : null"
        :label="showLabel ? label : null"
        :description="description"
        :invalid-feedback="errorMessage"
        :state="validationState">
        <vue-bootstrap-autocomplete
            v-model="quickEntry"
            :data="medications"
            :placeholder="placeholder"
            :minMatchingChars="2"
            :serializer="medicationSerializer"
            @hit="onMedicationSelected"
            @input="onInputChange"
            @keydown.native.enter.stop.prevent
            @keyup.native.enter.prevent="onEnterKey"
        >
            <template slot="suggestion" slot-scope="{ data, htmlText }">
                <div class="d-flex align-items-center">
                    <span class="ml-1" v-html="htmlText"></span>
                    <span class="ml-auto small text-muted">{{ data.code }}</span>
                </div>
            </template>
            <template #append>
                <b-button 
                    variant="success"
                    @click="parseEntry"
                    :disabled="!quickEntry || loading">
                    <b-spinner small v-if="loading"></b-spinner>
                    <i v-else class="fa-solid fa-wand-magic-sparkles"></i>
                    Parse
                </b-button>
                <b-button 
                    variant="warning" 
                    @click="clearEntry"
                    :disabled="!quickEntry">
                    <i class="fa-regular fa-arrows-rotate-reverse"></i>
                    Clear
                </b-button>
            </template>
        </vue-bootstrap-autocomplete>
    </b-form-group>
</template>

<script>
import axios from 'axios'
import { mapActions } from 'vuex'

export default {
    name: 'quick-entry-input',
    props: {
        value: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: 'Quick Entry'
        },
        showLabel: {
            type: Boolean,
            default: true
        },
        description: {
            type: String,
            default: 'Pattern: medication, strength, dosage/period, repeat(optional). Example: paracetamol, 500mg, 3d2tab/7, r3/30d'
        },
        placeholder: {
            type: String,
            default: 'Add quick entry...'
        },
        parseOnBlur: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            quickEntry: this.value,
            loading: false,
            errorMessage: '',
            validationState: null,
            medications: [],
            searchTimeout: null,
            latestSearchRequestId: 0,
            skipNextEnterParse: false,
            prescriptionForm: null
        }
    },
    created() {
        this.resetPrescriptionForm()
    },
    watch: {
        value(newVal) {
            this.quickEntry = newVal
        },
        quickEntry(newVal) {
            this.$emit('input', newVal)
            if (this.errorMessage && newVal) {
                this.validationState = null
                this.errorMessage = ''
            }
        }
    },
    methods: {
        ...mapActions('prescriptions', ['parseQuickEntry']),
        medicationSerializer(medication) {
            return medication.name
        },
        
        onMedicationSelected(medication) {
            this.quickEntry = medication.name + ', '
            this.medications = []
            this.$emit('selected-medication', medication)
            this.skipNextEnterParse = true
            setTimeout(() => {
                this.skipNextEnterParse = false
            }, 0)
        },

        onEnterKey() {
            if (this.skipNextEnterParse) {
                this.skipNextEnterParse = false
                return
            }
            this.parseEntry()
        },
        
        onInputChange() {
            const text = this.quickEntry.trim()
            if (!text) {
                this.medications = []
                return
            }
            
            const firstComma = text.indexOf(',')
            if (firstComma > 0) {
                this.medications = []
                return
            }
            
            const searchTerm = text
            
            if (searchTerm.length < 2) {
                this.medications = []
                return
            }
            
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.searchMedications(searchTerm)
            }, 300)
        },
        
        async searchMedications(query) {
            if (!query || query.length < 2) {
                this.medications = []
                return
            }

            const requestId = ++this.latestSearchRequestId
            try {
                const response = await axios.get('/api/catalogue/medications/', {
                    params: { search: query }
                })

                if (requestId !== this.latestSearchRequestId) {
                    return
                }
                this.medications = response.data.results || response.data || []
            } catch (error) {
                if (requestId !== this.latestSearchRequestId) {
                    return
                }
                this.medications = []
            }
        },

        createPrescriptionForm() {
            return Form.create({
                medication_id: null,
                is_chronic: false,
                other: "",
                strength: null,
                unit_id: null,
                frequency_usage: null,
                frequency_unit_id: null,
                dosage_amount: null,
                dosage_form_id: null,
                amount_of_time: null,
                delivery_form_id: null,
                instructions: "",
                stoppage_reason: "",
                repeat_prescription: 0,
                repeat_date_id: null,
                administrative_datetime: null,
                consistency_token: null
            })
        },
        resetPrescriptionForm() {
            this.prescriptionForm = this.createPrescriptionForm()
        },
        applyParsedDataToForm(data) {
            if (!this.prescriptionForm) {
                this.resetPrescriptionForm()
            }
            const source = data?.form || data || {}
            this.prescriptionForm.populate({
                medication_id: source.medication_id ?? null,
                is_chronic: Boolean(source.is_chronic),
                other: source.other || "",
                strength: source.strength ?? null,
                unit_id: source.unit_id ?? null,
                frequency_usage: source.frequency_usage ?? null,
                frequency_unit_id: source.frequency_unit_id ?? null,
                dosage_amount: source.dosage_amount ?? null,
                dosage_form_id: source.dosage_form_id ?? null,
                amount_of_time: source.amount_of_time ?? null,
                delivery_form_id: source.delivery_form_id ?? null,
                repeat_prescription: source.repeat_prescription ?? 0,
                repeat_date_id: source.repeat_date_id ?? null,
            })
        },
        normalizeParsedData(data) {
            if (!data || typeof data !== 'object') {
                return data
            }

            this.applyParsedDataToForm(data)
            const formData = this.prescriptionForm ? this.prescriptionForm.data() : (data.form && typeof data.form === 'object' ? data.form : data)
            const metaData = data.meta && typeof data.meta === 'object' ? data.meta : {}
            const warnings = Array.isArray(data.warnings) ? data.warnings : []

            return {
                ...formData,
                ...metaData,
                form: formData,
                meta: metaData,
                warnings
            }
        },
        
        async parseEntry() {
            if (this.loading) {
                return
            }

            if (!this.quickEntry || !this.quickEntry.trim()) {
                this.errorMessage = 'Please enter a prescription shortcode'
                this.validationState = false
                return
            }

            this.loading = true
            this.errorMessage = ''
            this.validationState = null

            try {
                const responseData = await this.parseQuickEntry({
                    entry: this.quickEntry
                })

                const parsedData = this.normalizeParsedData(responseData)

                if (parsedData) {
                    this.validationState = true
                    this.$emit('parsed', parsedData)
                    this.$emit('success', parsedData)
                    if (parsedData.warnings?.length) {
                        this.$emit('warning', parsedData.warnings)
                    }
                }
            } catch (error) {
                this.validationState = false
                
                if (error.response && error.response.data) {
                    this.errorMessage = error.response.data.error || 'Failed to parse prescription'
                } else {
                    this.errorMessage = 'Network error. Please try again.'
                }

                this.$emit('error', {
                    message: this.errorMessage,
                    response: error.response
                })
                this.$bvToast.toast(this.errorMessage, {
                    title: 'Parse Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 5000
                })
            } finally {
                this.loading = false
            }
        },
        
        clearEntry() {
            this.quickEntry = ''
            this.validationState = null
            this.errorMessage = ''
            this.resetPrescriptionForm()
            this.$emit('cleared')
        },

        onBlur() {
            if (this.parseOnBlur && this.quickEntry && this.quickEntry.trim()) {
                this.parseEntry()
            }
        },

        parse() {
            return this.parseEntry()
        },

        setEntry(text) {
            this.quickEntry = text
        }
    },
    beforeDestroy() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }
    }
}
</script>
